// Plain-English translation layer for the Research Hub reader surfaces.
//
// THE RULE (from the build brief): the user must NEVER see a UUID, node/edge/conflict
// id, table or `research_*` function name, a raw decimal in prose, or an internal enum
// string (`synthetic_only`, `contradicted_by_drift`, `no_signal_coverage`, …). Strength
// is a labelled bar or a word, never a number. Everything here is British English,
// sentence case, no emoji. Every translator falls back to a humanised string so an
// unmapped enum is still readable, never raw snake_case.

import { format, parseISO, differenceInCalendarDays } from "date-fns";

/* ------------------------------------------------------------------ tone ---- */
// The reader palette is teal-first. The only non-teal "pops" are the financial-gold
// (attention) and destructive (against/breach). "Good/on track" reassurance reads in
// teal. Tone never appears as colour alone — it always rides on a word or icon.
export type Tone = "neutral" | "attention" | "good" | "bad";

export function toneText(tone: Tone): string {
  switch (tone) {
    case "attention":
      return "text-[hsl(var(--financial-gold))]";
    case "bad":
      return "text-destructive";
    case "good":
      return "text-primary";
    default:
      return "text-foreground";
  }
}

export function toneDot(tone: Tone): string {
  switch (tone) {
    case "attention":
      return "bg-[hsl(var(--financial-gold))]";
    case "bad":
      return "bg-destructive";
    case "good":
      return "bg-primary";
    default:
      return "bg-muted-foreground/40";
  }
}

// Soft tinted surface for badges/cards — low-opacity so nothing screams.
export function toneSurface(tone: Tone): string {
  switch (tone) {
    case "attention":
      return "bg-[hsl(var(--financial-gold)/0.12)] text-[hsl(38_60%_32%)] border-[hsl(var(--financial-gold)/0.4)]";
    case "bad":
      return "bg-destructive/10 text-destructive border-destructive/30";
    case "good":
      return "bg-primary/10 text-primary border-primary/25";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

/* -------------------------------------------------------------- strength ---- */
export function strengthWord(v: number | null | undefined): string {
  const n = typeof v === "number" ? Math.abs(v) : 0;
  if (n >= 0.66) return "strong";
  if (n >= 0.33) return "moderate";
  if (n > 0) return "tentative";
  return "weak";
}

export function clamp01(v: number | null | undefined): number {
  const n = typeof v === "number" && Number.isFinite(v) ? v : 0;
  return Math.max(0, Math.min(1, Math.abs(n)));
}

/* ------------------------------------------------------------- direction ---- */
export type DirectionInfo = { word: "bullish" | "bearish" | ""; glyph: "▲" | "▼" | "" };

// Accepts a string ('bullish'/'bearish'/'positive'/'up'/'long'…) or a signed number.
export function directionInfo(dir: string | number | null | undefined): DirectionInfo {
  if (typeof dir === "number") {
    if (dir > 0) return { word: "bullish", glyph: "▲" };
    if (dir < 0) return { word: "bearish", glyph: "▼" };
    return { word: "", glyph: "" };
  }
  const d = (dir ?? "").toString().toLowerCase().trim();
  if (/(bull|positive|^up$|^long$|^\+|increase|rises?|higher)/.test(d)) return { word: "bullish", glyph: "▲" };
  if (/(bear|negative|^down$|^short$|^-|decrease|falls?|lower)/.test(d)) return { word: "bearish", glyph: "▼" };
  return { word: "", glyph: "" };
}

// "this scenario hurts / helps" from a sign value or word.
export function helpsHurts(sign: string | number | null | undefined): { word: "helps" | "hurts" | "mixed" | ""; tone: Tone } {
  if (typeof sign === "number") {
    if (sign > 0) return { word: "helps", tone: "good" };
    if (sign < 0) return { word: "hurts", tone: "bad" };
    return { word: "mixed", tone: "neutral" };
  }
  const s = (sign ?? "").toString().toLowerCase().trim();
  if (/(help|support|positive|tailwind|benefit|^\+)/.test(s)) return { word: "helps", tone: "good" };
  if (/(hurt|hit|negative|headwind|loss|adverse|^-)/.test(s)) return { word: "hurts", tone: "bad" };
  if (!s) return { word: "", tone: "neutral" };
  return { word: "mixed", tone: "neutral" };
}

/* --------------------------------------------------------------- recency ---- */
export function safeDate(d: string | null | undefined): Date | null {
  if (!d) return null;
  try {
    const parsed = typeof d === "string" ? parseISO(d) : new Date(d);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}

export function formatAsOf(d: string | null | undefined): string {
  const dt = safeDate(d);
  return dt ? `as of ${format(dt, "d MMM yyyy")}` : "date unknown";
}

export function formatDay(d: string | null | undefined): string {
  const dt = safeDate(d);
  return dt ? format(dt, "d MMM yyyy") : "—";
}

export function daysOld(d: string | null | undefined): number | null {
  const dt = safeDate(d);
  return dt ? Math.max(0, differenceInCalendarDays(new Date(), dt)) : null;
}

export function recencyLine(d: string | null | undefined): string {
  const dt = safeDate(d);
  if (!dt) return "date unknown";
  const days = daysOld(d);
  const base = `as of ${format(dt, "d MMM yyyy")}`;
  if (days == null) return base;
  if (days === 0) return `${base} · today`;
  if (days === 1) return `${base} · 1 day old`;
  return `${base} · ${days} days old`;
}

/* ------------------------------------------------------------------- num ---- */
export function pct(v: number | null | undefined, digits = 0): string {
  if (typeof v !== "number" || !Number.isFinite(v)) return "—";
  return `${v.toFixed(digits)}%`;
}

// hit_rate is 0..1 → "right about 67% of the time"
export function hitRatePct(hitRate: number | null | undefined): number | null {
  if (typeof hitRate !== "number" || !Number.isFinite(hitRate)) return null;
  return Math.round(hitRate * 100);
}

// Round a "typical miss" (mean_abs_error) for display in its own units.
export function roundMiss(mae: number | null | undefined): string | null {
  if (typeof mae !== "number" || !Number.isFinite(mae)) return null;
  return mae >= 10 ? mae.toFixed(0) : mae.toFixed(2);
}

/* --------------------------------------------------------------- humanise ---- */
// Last-resort: turn any leftover enum/snake_case into a readable sentence-case phrase.
export function humanise(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .toString()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

/* ------------------------------------------------- field translators -------- */
export type Labelled = { label: string; tone: Tone };

// thesis/edge health_status
export function healthStatus(s: string | null | undefined): Labelled {
  switch ((s ?? "").toLowerCase()) {
    case "supported":
      return { label: "Market agrees so far", tone: "good" };
    case "stale":
      return { label: "Evidence is getting old", tone: "attention" };
    case "single_source":
      return { label: "Leans on one source", tone: "attention" };
    case "live_contradiction":
      return { label: "The market is moving against this", tone: "bad" };
    case "no_signal_coverage":
      return { label: "No live signal to test this yet", tone: "neutral" };
    default:
      return { label: humanise(s) || "Status unknown", tone: "neutral" };
  }
}

// house-view consistency verdict
export function verdict(s: string | null | undefined): Labelled {
  switch ((s ?? "").toLowerCase()) {
    case "consistent":
      return { label: "Holds together", tone: "good" };
    case "contradicted_by_newer_thesis":
      return { label: "A newer view of ours disagrees", tone: "attention" };
    case "contradicted_by_drift":
      return { label: "The market has moved against it", tone: "bad" };
    default:
      return { label: humanise(s), tone: "attention" };
  }
}

// coverage support_tier (also no_source=true → inferred only)
export function supportTier(s: string | null | undefined, noSource?: boolean): Labelled {
  const key = (s ?? "").toLowerCase();
  if (noSource || key === "synthetic_only")
    return { label: "Inferred only — no real research behind it yet", tone: "attention" };
  if (key === "sourced") return { label: "Backed by research", tone: "good" };
  if (key === "uncovered") return { label: "Nothing covers this yet", tone: "bad" };
  return { label: humanise(s) || "Coverage unknown", tone: "neutral" };
}

// falsify / against-evidence disagreement_kind (channel badge)
export function disagreementKind(s: string | null | undefined): string {
  switch ((s ?? "").toLowerCase()) {
    case "opposing_thesis":
      return "An opposing view";
    case "open_conflict":
      return "A logged conflict";
    case "opposing_sign_causal":
      return "A driver pushing the other way";
    default:
      return humanise(s) || "Counter-evidence";
  }
}

// conflict.kind (between two views)
export function conflictKind(s: string | null | undefined): string {
  switch ((s ?? "").toLowerCase()) {
    case "opposing_thesis":
      return "Opposing views";
    case "open_conflict":
      return "A logged conflict";
    case "opposing_sign_causal":
      return "Drivers point opposite ways";
    case "direct_contradiction":
      return "A direct contradiction";
    default:
      return humanise(s) || "A disagreement";
  }
}

// conflict resolution margin_band
export function marginBand(s: string | null | undefined): string {
  switch ((s ?? "").toLowerCase()) {
    case "clear":
      return "Clear winner";
    case "close":
    case "narrow":
      return "Close call";
    default:
      return humanise(s);
  }
}

// thesis-health severity
export function severity(s: string | null | undefined): Labelled {
  switch ((s ?? "").toLowerCase()) {
    case "high":
      return { label: "Needs attention", tone: "bad" };
    case "medium":
      return { label: "Worth noting", tone: "attention" };
    case "low":
      return { label: "Minor", tone: "neutral" };
    default:
      return { label: humanise(s), tone: "neutral" };
  }
}

// PM positioning action_hint (qualitative pill — NO sizes implied)
export function actionHint(s: string | null | undefined): Labelled {
  switch ((s ?? "").toLowerCase()) {
    case "add":
      return { label: "Consider adding", tone: "good" };
    case "trim":
      return { label: "Consider trimming", tone: "attention" };
    case "hedge":
      return { label: "Consider hedging", tone: "attention" };
    case "review":
      return { label: "Worth a review", tone: "attention" };
    case "hold":
      return { label: "Hold", tone: "neutral" };
    default:
      return { label: humanise(s) || "No action flagged", tone: "neutral" };
  }
}

// thesis_confidence → a held-ness word
export function confidenceWord(s: string | number | null | undefined): string {
  if (typeof s === "number") {
    if (s >= 0.66) return "strongly held";
    if (s >= 0.33) return "held";
    if (s > 0) return "qualified";
    return "tentative";
  }
  switch ((s ?? "").toLowerCase()) {
    case "high":
    case "strong":
      return "strongly held";
    case "medium":
    case "moderate":
      return "held";
    case "low":
      return "qualified";
    case "tentative":
      return "tentative";
    default:
      return humanise(s) || "held";
  }
}

// delivered-output output_kind → friendly title
export function outputKind(s: string | null | undefined): string {
  switch ((s ?? "").toLowerCase()) {
    case "cio_ic_brief":
    case "ic_brief":
      return "Investment committee brief";
    case "pm_brief":
      return "Portfolio brief";
    case "analyst_deepdive":
      return "Analyst deep-dive";
    case "morning_brief":
      return "Morning brief";
    case "adviser_comms":
      return "Adviser note";
    case "zoom_doc":
      return "Meeting note";
    case "ic_pack":
      return "IC pack";
    default:
      return humanise(s) || "Brief";
  }
}

// prebrief surface → which lens
export function surfaceLabel(s: string | null | undefined): string {
  switch ((s ?? "").toLowerCase()) {
    case "cio":
      return "Chief investment view";
    case "pm":
      return "Portfolio view";
    case "analyst":
      return "Analyst view";
    case "adviser":
      return "Adviser view";
    default:
      return humanise(s) || "Daily digest";
  }
}

// source credibility factor → trust chip
export function trustChip(factor: number | null | undefined): { label: string; tone: Tone; glyph: string } {
  if (typeof factor !== "number" || !Number.isFinite(factor))
    return { label: "Reliability not scored", tone: "neutral", glyph: "" };
  if (factor > 1.05) return { label: "High reliability", tone: "good", glyph: "▲" };
  if (factor < 0.95) return { label: "Lower reliability", tone: "attention", glyph: "▼" };
  return { label: "Neutral reliability", tone: "neutral", glyph: "" };
}

// Capture-suggestion type (research_suggestion_type enum) → plain phrase.
// The user reviews machine-proposed writes to the graph/ledger; the type tells them
// what kind of write is proposed, never the raw enum string.
export function suggestionType(s: string | null | undefined): string {
  switch ((s ?? "").toLowerCase()) {
    case "kg_node":
      return "New entity";
    case "kg_edge":
      return "New relationship";
    case "thesis_attachment":
      return "Evidence attachment";
    case "macro_prediction":
      return "Forecast for the ledger";
    case "drift_flag":
      return "Contradiction flag";
    case "table_data":
      return "Extracted table";
    default:
      return humanise(s) || "Suggestion";
  }
}

// Capture-suggestion status (research_suggestion_status enum) → labelled tone.
export function suggestionStatus(s: string | null | undefined): Labelled {
  switch ((s ?? "").toLowerCase()) {
    case "proposed":
      return { label: "Awaiting you", tone: "attention" };
    case "accepted":
      return { label: "Accepted", tone: "good" };
    case "rejected":
      return { label: "Rejected", tone: "neutral" };
    case "superseded":
      return { label: "Superseded", tone: "neutral" };
    case "expired":
      return { label: "Expired", tone: "neutral" };
    default:
      return { label: humanise(s) || "Status unknown", tone: "neutral" };
  }
}

// Confidence (0..1) → a held-ness tier word + tone. Numbers never shown to the user
// as a bare decimal — this is the labelled equivalent for a suggestion's confidence.
export function confidenceTier(v: number | null | undefined): Labelled {
  if (typeof v !== "number" || !Number.isFinite(v))
    return { label: "Confidence not scored", tone: "neutral" };
  if (v >= 0.75) return { label: "High confidence", tone: "good" };
  if (v >= 0.5) return { label: "Moderate confidence", tone: "attention" };
  return { label: "Low confidence", tone: "attention" };
}

// connector health_scan signal → plain phrase
export function connectorSignal(s: string | null | undefined): string {
  switch ((s ?? "").toLowerCase()) {
    case "stale":
      return "behind";
    case "firecrawl_402":
      return "out of credits";
    case "otter_4xx":
      return "connection issue";
    case "ok":
    case "healthy":
      return "up to date";
    default:
      return humanise(s) || "status unknown";
  }
}

/* --------------------------------------------------- sentence builders ------ */
// Belief sentence from a from/to/direction triple, e.g. "Bearish: energy prices → inflation".
export function beliefSentence(
  from: string | null | undefined,
  to: string | null | undefined,
  dir?: string | number | null,
): string {
  const f = (from ?? "").toString().trim();
  const t = (to ?? "").toString().trim();
  const core = f && t ? `${f} → ${t}` : f || t || "a view we hold";
  const d = directionInfo(dir);
  return d.word ? `${d.word[0].toUpperCase()}${d.word.slice(1)}: ${core}` : core;
}

/* --------------------------------------------- id → human label fallbacks --- */
// THE RULE again, applied to raw database identifiers: a UUID/hex string must NEVER
// be the whole label. Diagnostic pages used to render `id.slice(0, 8)` directly —
// that leaks a meaningless hex fragment. These helpers always produce a readable
// phrase, only ever showing a short id IN BRACKETS as a last-resort disambiguator.

// Trim a long human label so it never overruns a table cell, keeping it readable.
export function truncateLabel(label: string | null | undefined, max = 30): string {
  const s = (label ?? "").toString().trim();
  if (!s) return "";
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

// Short id disambiguator, e.g. "abc123de" from a UUID. Never shown alone.
export function shortId(id: string | null | undefined): string {
  return (id ?? "").toString().replace(/-/g, "").slice(0, 8);
}

// A node reference that may be unresolved. With a label → the (truncated) label.
// Without → "unknown node (abc123de)" — readable, with the id only as a hint,
// never a bare hex string.
export function nodeLabel(
  label: string | null | undefined,
  id?: string | null | undefined,
): string {
  const l = truncateLabel(label);
  if (l) return l;
  const sid = shortId(id);
  return sid ? `unknown node (${sid})` : "unknown node";
}

// An edge resolved to labels, rendered as plain prose: "Energy → Inflation (supports)".
// `edge` carries from_label/to_label/edge_type/polarity (the kg_edges_with_labels
// shape) plus the raw edge id so an unresolved endpoint still reads sensibly.
export type EdgeForDisplay = {
  edge_id?: string | null;
  from_label?: string | null;
  to_label?: string | null;
  edge_type?: string | null;
  polarity?: string | null;
  from_id?: string | null;
  to_id?: string | null;
};

export function formatEdgeForDisplay(edge: EdgeForDisplay | null | undefined): string {
  if (!edge) return "Relationship not found";
  const from = nodeLabel(edge.from_label, edge.from_id);
  const to = nodeLabel(edge.to_label, edge.to_id);
  // Prefer a polarity word (helps/hurts) when present, else the relationship type.
  const kindRaw = edge.polarity || edge.edge_type || "";
  const kind = humanise(kindRaw).toLowerCase();
  const core = `${from} → ${to}`;
  return kind ? `${core} (${kind})` : core;
}

// A source-chain reference: "child of 'Artemis Q3 holdings'" — never "child of abc123de".
// Falls back to a bracketed short id only when no parent title is known.
export function sourceChainLabel(
  parentTitle: string | null | undefined,
  parentId?: string | null | undefined,
): string {
  const t = truncateLabel(parentTitle, 60);
  if (t) return `child of “${t}”`;
  const sid = shortId(parentId);
  return sid ? `child of an earlier source (${sid})` : "child of an earlier source";
}

// A one-line human summary of an AI run, instead of a raw run UUID.
// e.g. "Referee verdict · claude-3-5-sonnet · 3 Jun 2026".
export function runSummary(run: {
  action?: string | null;
  model?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
  id?: string | null;
} | null | undefined): string {
  if (!run) return "Run";
  const parts: string[] = [];
  const role = humanise(run.action);
  if (role) parts.push(role);
  if (run.model) parts.push(run.model);
  const when = run.completed_at ?? run.created_at;
  const dt = safeDate(when);
  if (dt) parts.push(format(dt, "d MMM yyyy"));
  if (parts.length) return parts.join(" · ");
  const sid = shortId(run.id);
  return sid ? `Run (${sid})` : "Run";
}

// A human ingest summary built from an ingest response payload, instead of "→ abc123de".
// e.g. "Ingested 2,450 words from “Artemis Q3 holdings”".
export function ingestSummary(payload: {
  word_count?: number | null;
  words?: number | null;
  title?: string | null;
  source_title?: string | null;
  source_type?: string | null;
  source_id?: string | null;
} | null | undefined): string {
  if (!payload) return "Ingested a new source";
  const words = payload.word_count ?? payload.words ?? null;
  const title = truncateLabel(payload.title ?? payload.source_title, 60);
  const wordPart =
    typeof words === "number" && Number.isFinite(words)
      ? `${words.toLocaleString("en-GB")} word${words === 1 ? "" : "s"}`
      : null;
  const fromPart = title
    ? `from “${title}”`
    : payload.source_type
      ? `from a ${humanise(payload.source_type).toLowerCase()}`
      : null;
  if (wordPart && fromPart) return `Ingested ${wordPart} ${fromPart}`;
  if (wordPart) return `Ingested ${wordPart}`;
  if (fromPart) return `Ingested a source ${fromPart}`;
  return "Ingested a new source";
}
